import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalRef, SidePanel } from '@anthem/uxd/modal';
import { InfoListService } from '../../../common/components/info-list/services/infoListSvc';
import { IAssignCareLoader } from '../../../common/interfaces/iAssignCare';
import { IWindow } from '../../../common/interfaces/iWindow';
import { IOptions } from '../../interfaces/iAppMetadata';
import { IAddress } from './../../../care-circle/interfaces/iAddress';
import { IProvider } from './../../../care-circle/interfaces/iCareProvider';
import { CareCircleService } from './../../../care-circle/services/careCircleSvc';
import { CareCircleUtil } from './../../../care-circle/utilities/careCircleUtil';
import { BaseComponent } from './../../../common/components/base-component/baseCmp';
import { Locale } from './../../../common/constants/app-constants';
import { ADDCARETEAM_FAILURE_MESSAGE, ADDCARETEAM_SUCCESS_MESSAGE, ALERT_TYPE, PROVIDER_TYPE } from './../../../fad/search-providers/values/providerSearchConstants';
import { CommonUtil } from './../../../fad/utilities/commonUtil';
import { INFOBOX_EVENTS } from './../../enums/infoBox';
import { IBaseCode } from './../../interfaces/iBaseCode';
import { IMessage } from './../../interfaces/iMessage';
import { EventHandler } from './../../services/eventHandler';
import { AppSession } from './../../values/appSession';
import { ContentHelper } from './../../values/contentHelper';

@Component({
  moduleId: module.id,
  selector: 'app-assign-care',
  templateUrl: './assignCareCmp.html'
})
export class AssignCareComponent extends BaseComponent implements OnInit {
  @Input()
  btnClass: string = 'btn-secondary';
  @Input()
  btnAriaLabel: string = '';
  @Input()
  index: number = 0;
  @Input() pageName: string;
  @Input()
  eventKey: string = '';
  @Input()
  providerInfo: any;
  @Output()
  loader: EventEmitter<IAssignCareLoader> = new EventEmitter<IAssignCareLoader>();
  @ViewChild('assignProviderModal')
  assignProviderModal: TemplateRef<HTMLElement>;
  isMemberSecure: boolean = false;
  tagName: string = '';
  addCareAdobeTag: string = '';
  cancelCareAdobeTag: string = '';
  locale: string;
  removeProviderPanelRef: ModalRef<any, any>;
  memberOptions: any;
  selectedMbr: IOptions;
  addProviderConfirm: string = '';
  ariaLabelForAddCareTeam: string = '';
  isEmulation: boolean = false;
  providerIndex: number = 0;

  constructor(
    private _careTeamSvc: CareCircleService,
    @Inject(AppSession)
    private _appSession: AppSession,
    @Inject('Window')
    private _window: IWindow,
    private _eventHandler: EventHandler,
    private _route: ActivatedRoute,
    private _contentHelper: ContentHelper,
    private _sidePanel: SidePanel,
    private _titlecasePipe: TitleCasePipe,
    private _infoListSvc: InfoListService
  ) {
    super(_route, _eventHandler, _appSession, _contentHelper, 'PFSearchContainerComponent');
  }

  ngOnInit() {
    if (this.waitUntilAppReload) {
      return;
    }
    if (this._appSession) {
      this.isEmulation = this._appSession.isEmulation;
      this.locale = this._appSession.metaData?.locale ? this._appSession.metaData.locale.toString() : Locale.ENGLISH;

      this.isMemberSecure = CommonUtil.isMemberSecure(this._appSession);

      this.bindMemberInfo();
    }

    this.updateAnalyticsTag();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      typeof changes['eventKey'] !== 'undefined' &&
      changes['eventKey'] !== null &&
      typeof changes['eventKey'].currentValue !== 'undefined' &&
      changes['eventKey'].currentValue !== null &&
      changes['eventKey'].currentValue !== ''
    ) {
      const eventKey = changes['eventKey'].currentValue as string;
      if (this._appSession) {
        this.bindMemberInfo();
      }
      this.handleEvents(eventKey);
    }
  }

  addCareTeam() {
    const assignCareLoaderObj = {} as IAssignCareLoader;
    assignCareLoaderObj.loader = true;
    assignCareLoaderObj.apiError = false;
    this.loader.emit(assignCareLoaderObj);

    const _providerReq = {} as IProvider;
    this.providerIndex = this.providerInfo.actualIndex;
    _providerReq.providerId = this.providerInfo.providerIdentifier;
    _providerReq.providerDisplayNm = this.providerInfo.providerName ? this.providerInfo.providerName : '';
    if (this.providerInfo.firstName) {
      _providerReq.providerFirstNm = this.providerInfo.firstName;
      _providerReq.providerLastNm = this.providerInfo.lastName ? this.providerInfo.lastName : '';
      _providerReq.providerMiddleNm = this.providerInfo.middleName ? this.providerInfo.middleName : '';
    } else {
      _providerReq.providerFirstNm = _providerReq.providerDisplayNm;
    }

    _providerReq.nickName = '';
    _providerReq.nationalProviderId = this.providerInfo.nationalProviderIdentifier;
    _providerReq.providerTypeCodes = this.providerInfo.providerTypeCodeList;
    _providerReq.address = {
      addressId: this.providerInfo.location.address.addressId,
      addressLineOne: this.providerInfo.location.address.addressOne,
      latitude: this.providerInfo.location.address.latitude,
      longitude: this.providerInfo.location.address.longitude,
      //addressLineTwo: this.providerInfo.location.address.addressTwo,
      stateCd: {
        code: this.providerInfo.location.address.state,
        name: this.providerInfo.location.address.state
      } as IBaseCode,
      city: this.providerInfo.location.address.city,
      zipCd: this.providerInfo.location.address.postalCode,
      phone: this.providerInfo.location.address.phone
    } as IAddress;

    if (this.providerInfo.providerTypeCodeList && this.providerInfo.providerTypeCodeList.length === 1 && this.providerInfo.providerTypeCodeList[0] === PROVIDER_TYPE.PHARMACY) {
      const _specialties: IBaseCode[] = [];
      _specialties.push({
        code: '',
        name: ''
      } as IBaseCode);
      _providerReq.specialtyCd = _specialties;
    } else {
      if (this.providerInfo.specialties && this.providerInfo.specialties.length > 0) {
        const _specialties: IBaseCode[] = [];
        for (const specialty of this.providerInfo.specialties || []) {
          _specialties.push({
            code: specialty.specialtyCode,
            name: this.locale === Locale.SPANISH ? specialty.specialtyCodeEnName : specialty.specialtyCodeName
          } as IBaseCode);
        }
        _providerReq.specialtyCd = _specialties;
      }
    }

    _providerReq.source = 'findcare';
    _providerReq.isRemoved = false;

    const _disclaimerCodes = this._infoListSvc.disclaimerCodes;
    this._infoListSvc.reset();
    let disclaimerCodes: IMessage[] = [];

    const _selectedMemberUid = this.selectedMbr.mbrUid;
    this._careTeamSvc.addCareTeam(CareCircleUtil.buildAddCareTeamRequest(this._appSession, _providerReq, _selectedMemberUid)).then(
      (resp: any) => {
        if (resp) {
          // Assign care
        }

        const _index = _disclaimerCodes.findIndex((x) => x.contentId === ADDCARETEAM_SUCCESS_MESSAGE);
        if (_index > -1) {
          _disclaimerCodes.splice(_index, 1);
        }
        disclaimerCodes = _disclaimerCodes;
        const _message = {
          contentId: ADDCARETEAM_SUCCESS_MESSAGE,
          content: this.buildMessage(true),
          type: ALERT_TYPE.POSITIVE,
          focus: true
        } as IMessage;

        assignCareLoaderObj.loader = false;
        this.loader.emit(assignCareLoaderObj);
        this.closeAssignProviderModal();

        disclaimerCodes.unshift(_message);
        this._infoListSvc.setInfoList(disclaimerCodes);
      },
      (error: any) => {
        assignCareLoaderObj.loader = false;
        assignCareLoaderObj.apiError = true;
        this.loader.emit(assignCareLoaderObj);

        const _index = _disclaimerCodes.findIndex((x) => x.contentId === ADDCARETEAM_FAILURE_MESSAGE);
        if (_index > -1) {
          _disclaimerCodes.splice(_index, 1);
        }
        disclaimerCodes = _disclaimerCodes;
        const _message = {
          contentId: ADDCARETEAM_FAILURE_MESSAGE,
          content: this.buildMessage(false),
          type: ALERT_TYPE.NEGATIVE,
          focus: true
        } as IMessage;
        this.closeAssignProviderModal();
        disclaimerCodes.unshift(_message);
        this._infoListSvc.setInfoList(disclaimerCodes);
      }
    );

    return false;
  }

  buildMessage(isSuccess: boolean) {
    let _message = '';
    if (this.selectedMbr) {
      const _memberName = this.titleCase(this.selectedMbr.firstName) + ' ' + this.titleCase(this.selectedMbr.lastName);
      const _providerName = this.getProviderName();

      if (isSuccess) {
        _message = this.content.commonContents.labels[ADDCARETEAM_SUCCESS_MESSAGE];
      } else {
        _message = this.content.commonContents.labels[ADDCARETEAM_FAILURE_MESSAGE];
      }
      _message = _message.replace(/{MEMBER}/gi, _memberName.trim()).replace(/{PROVIDER}/gi, _providerName);
    }
    return _message;
  }

  getProviderName() {
    let _providerName = '';
    _providerName = this.titleCase(this.providerInfo.providerName);
    return _providerName.trim();
  }

  updateAnalyticsTag() {
    if (this.pageName === 'results') {
      this.tagName = 'addTeamSummaryFindCare';
      this.addCareAdobeTag = 'addTeamSummaryModalFindCare';
      this.cancelCareAdobeTag = 'cancelAddTeamSummaryModalFindCare';
    } else {
      this.tagName = 'addTeamDetailFindCare';
      this.addCareAdobeTag = 'addTeamDetailModalFindCare';
      this.cancelCareAdobeTag = 'cancelAddTeamDetailModalFindCare';
    }
  }

  bindMemberInfo() {
    if (
      this._appSession &&
      this._appSession.metaData &&
      this._appSession.metaData.appContract &&
      this._appSession.metaData.appContract.memberList &&
      this._appSession.metaData.appContract.memberList.length > 0
    ) {
      const memberList = this._appSession.metaData.appContract.memberList.filter((x: any) => x.mbrUid !== '');
      if (memberList && memberList.length > 0) {
        this.memberOptions = this.buildMemberOptions(memberList);
        this.selectedMbr = this.getSelectedMember(this._appSession.metaData.appContract.mbrUid);

        const _memberName = this.titleCase(this.selectedMbr.firstName) + ' ' + this.titleCase(this.selectedMbr.lastName);
        const _providerName = this.getProviderName();
        this.addProviderConfirm = this.content.commonContents.labels.addCareProviderContent.replace(/{PROVIDER}/gi, _providerName).replace(/{MEMBER}/gi, _memberName.trim());
        this.ariaLabelForAddCareTeam = this.content.commonContents.labels.ariaLabelForAddCareTeam.replace(/{PROVIDER}/gi, _providerName).replace(/{MEMBER}/gi, _memberName.trim());
      }
    }
  }

  buildMemberOptions(result: any) {
    const _memberOptions = [];
    result.forEach((x: any) => {
      _memberOptions.push({
        label:
          x.relationship && x.relationship !== null
            ? this.titleCase(x.firstName) + ' ' + this.titleCase(x.lastName) + ' (' + this.titleCase(x.relationship) + ')'
            : this.titleCase(x.firstName) + ' ' + this.titleCase(x.lastName),
        value: x.mbrUid,
        id: 'rdb-mbr-id-' + x.mbrUid,
        name: 'rdb-mbr-name-' + x.mbrUid
      });
    });
    return _memberOptions;
  }

  onMemberChange(mbr: any) {
    this.selectedMbr = this.getSelectedMember(mbr.value);
  }

  getSelectedMember(mbrUid: string) {
    let _selectedMember: IOptions;
    if (
      this._appSession &&
      this._appSession.metaData &&
      this._appSession.metaData.appContract &&
      this._appSession.metaData.appContract.memberList &&
      this._appSession.metaData.appContract.memberList.length > 0
    ) {
      const _memberList = this._appSession.metaData.appContract.memberList;
      mbrUid = mbrUid === '' ? this._appSession.metaData.appContract.mbrUid : mbrUid;
      if (mbrUid) {
        const mbr = _memberList.filter((x: any) => x.mbrUid === mbrUid);
        if (mbr && mbr.length > 0) {
          _selectedMember = mbr[0];
        }
      }
      if (!_selectedMember) {
        _selectedMember = _memberList.filter((x: any) => x.isChecked === true)[0];
      }
    }
    return _selectedMember;
  }

  titleCase(name: string) {
    return this._titlecasePipe.transform(name);
  }

  openSidePanel() {
    this.removeProviderPanelRef = this._sidePanel.open('right', this.assignProviderModal);
    return false;
  }

  closeAssignProviderModal() {
    this.removeProviderPanelRef.close();
  }

  /**
   * Function to take actions based on event key
   * @param eventKey
   */
  private handleEvents(eventKey: string) {
    switch (eventKey) {
      case INFOBOX_EVENTS.ASSIGN_CARE: {
        this.openSidePanel();
        break;
      }
      default:
        break;
    }
  }
}
